import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import Colors from '../../utils/color';
import ActionCenter from '../ActionCenter';
import CustomerPulse from '../CustomerPulse';
import LiveTranscriptCard from './LiveTranscriptCard';
import useLeftStackCollapse from './hooks/useLeftStackCollapse.hooks';

// Space to leave between the expanded transcript overlay and the right cart
// pane: the resize handle (spacing(1)) + the row's right padding (spacing(1)).
const OVERLAY_RIGHT_GAP = 16;

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  leftPane: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Left card gets top space; only the center attaches to the header.
    paddingTop: spacing(1),
  },
  // Single left card: transcript section + reminder section + AI status bar,
  // each an internal white card under its heading (mirrors the mockup).
  leftCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F9FAFB',
    border: `${spacing(0.125)} solid ${Colors.alto}`,
    borderRadius: spacing(1),
    padding: spacing(1.5),
  },
  // AI-supervised takeover: the whole left card lifts out and overlays the
  // left + center area (stops before the right Agent Cart), so the transcript
  // gets the room while the reminder + AI status sections stay visible. The
  // `right` offset is set inline from the measured right-pane width so it stays
  // correct after the panes are resized.
  leftCardExpanded: {
    position: 'absolute',
    top: spacing(1),
    left: spacing(1),
    bottom: 0,
    zIndex: 5,
  },
  // Wraps each card inside a vertical panel: fills the panel height so each
  // card's OWN internal scroll works. overflow hidden (not auto) — the cards
  // scroll internally now, so this wrapper must never show its own (outer)
  // scrollbar, which otherwise appeared beside a collapsed hug-height card.
  leftStackPane: {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    overflowX: 'hidden',
  },
  // Horizontal draggable divider between the stacked left-pane cards (Customer
  // Pulse / Live Transcript / Action Center).
  vResizeHandle: {
    height: spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    '&::after': {
      content: '""',
      height: 3,
      width: spacing(5),
      borderRadius: 2,
      backgroundColor: Colors.silverChalice,
      transition: 'background-color 120ms ease',
    },
    '&:hover::after, &[data-resize-handle-active]::after': {
      backgroundColor: Colors.readBack,
    },
  },
}));

interface LeftSidebarProps {
  // Per-agent storage key; the nested vertical stack persists under a suffix.
  paneStorageKey: string;
  // Transcript takeover: the whole left card lifts out and overlays the
  // left + center area.
  isExpanded: boolean;
  onCollapseTranscript: () => void;
  // Measured right-pane width — the takeover overlay stops before it.
  rightPaneWidth: number;
}

/**
 * LEFT SIDEBAR of the HITL console. One card holding a drag-to-resizable
 * vertical stack of Customer Pulse / Live Transcript / Action Center (sizes
 * persist per agent via autoSaveId). The whole card lifts into a takeover
 * overlay while the transcript is expanded, stopping before the right cart
 * pane. Collapse-sync between each card's chevron and its panel lives in
 * useLeftStackCollapse.
 */
const LeftSidebar = ({
  paneStorageKey,
  isExpanded,
  onCollapseTranscript,
  rightPaneWidth,
}: LeftSidebarProps) => {
  const classes = useStyles();
  const {
    cpOpen,
    setCpOpen,
    acOpen,
    setAcOpen,
    ltOpen,
    setLtOpen,
    cpPanelRef,
    acPanelRef,
    ltPanelRef,
  } = useLeftStackCollapse();

  return (
    <div className={classes.leftPane}>
      <div
        className={`${classes.leftCard} ${
          isExpanded ? classes.leftCardExpanded : ''
        }`}
        style={
          isExpanded
            ? {
                right: `calc(${rightPaneWidth}px + ${OVERLAY_RIGHT_GAP}px)`,
              }
            : undefined
        }
      >
        {/* Left-pane cards (Customer Pulse / Live Transcript / Action Center)
            are drag-to-resizable (vertical twin of the 3-pane layout); sizes
            persist per agent via autoSaveId. */}
        <PanelGroup
          direction="vertical"
          autoSaveId={`${paneStorageKey}:left-stack`}
        >
          {/* collapsedSize is larger than the sibling cards' (7): the
              collapsed Customer Pulse strip keeps its AI status band
              below the header, so it needs ~2x the height. Stays below
              minSize (12) so collapse still snaps in. */}
          <Panel
            id="cp"
            order={1}
            ref={cpPanelRef}
            collapsible
            collapsedSize={11}
            defaultSize={18}
            minSize={12}
            onCollapse={() => setCpOpen(false)}
            onExpand={() => setCpOpen(true)}
          >
            <div className={classes.leftStackPane}>
              <CustomerPulse open={cpOpen} onOpenChange={setCpOpen} />
            </div>
          </Panel>
          <PanelResizeHandle className={classes.vResizeHandle} />
          <Panel
            id="lt"
            order={2}
            ref={ltPanelRef}
            collapsible
            collapsedSize={7}
            defaultSize={52}
            minSize={15}
            onCollapse={() => setLtOpen(false)}
            onExpand={() => setLtOpen(true)}
          >
            <div className={classes.leftStackPane}>
              <LiveTranscriptCard
                isExpanded={isExpanded}
                onCollapse={onCollapseTranscript}
                open={ltOpen}
                onOpenChange={setLtOpen}
              />
            </div>
          </Panel>
          <PanelResizeHandle className={classes.vResizeHandle} />
          <Panel
            id="ac"
            order={3}
            ref={acPanelRef}
            collapsible
            collapsedSize={7}
            defaultSize={30}
            minSize={12}
            onCollapse={() => setAcOpen(false)}
            onExpand={() => setAcOpen(true)}
          >
            <div className={classes.leftStackPane}>
              <ActionCenter open={acOpen} onOpenChange={setAcOpen} />
            </div>
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
};

export default LeftSidebar;
